import { Grid, Tooltip, Typography } from "@mui/material"
import React, { FormEvent, useCallback, useContext } from "react"
import { Box } from "@mui/system"
import { BSFormDialog } from "../../../../../../../../../components/dialog/BSFormDialog"
import { customPalette } from "../../../../../../../../../../theme"
import { FormNumberInput } from "../../../../../../../../../components/inputs/number-input/FormNumberInput"
import { stringToNumber } from "../../../../../../../../../core/services/helper-service"
import { BSPatioContext } from "../context/BSPatioContext"
import { BSPatioFormContext } from "../context/BSPatioFormContext"
import { InfoContext } from "../../../../../../../../../components/layout/InfoSnackbar"

interface IProps {
  isOpen: boolean
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>
}

export function BSPatioCreationFormDialog({ isOpen, setIsOpen }: Readonly<IProps>): React.JSX.Element {
  const openInfoSnackbar = useContext(InfoContext)
  const { isEditing, isSubmitting, submitBSPatioDtoList, bsPatioList } = useContext(BSPatioContext)
  const {
    selectedBSPatioForm,
    errors,
    setErrors,
    isDisplayNewBSPatioForm,
    validateBSPatioForm,
    setIsDisplayNewBSPatioForm,
    handleFieldChange,
    resetForm,
    bsPatioFormToDto,
  } = useContext(BSPatioFormContext)

  const handleClose: () => void = useCallback(() => {
    setIsOpen(false)
    setIsDisplayNewBSPatioForm(false)
    setErrors({})
  }, [setIsDisplayNewBSPatioForm, setErrors, setIsOpen])

  const handleSubmit: (event?: FormEvent<HTMLFormElement> | FormEvent<HTMLDivElement>) => Promise<any> = useCallback(
    (event) => {
      if (event) {
        event.preventDefault()
        event.stopPropagation()
      }

      if (isEditing) {
        openInfoSnackbar("Veuillez finaliser la modification en cours.")
      } else if (isDisplayNewBSPatioForm) {
        openInfoSnackbar("Veuillez finaliser l'ajout en cours.")
      } else if (validateBSPatioForm(selectedBSPatioForm)) {
        const newList = [...bsPatioList, bsPatioFormToDto(selectedBSPatioForm)]
        return submitBSPatioDtoList(newList).then(() => {
          setIsOpen(false)
          resetForm()
        })
      }

      return Promise.resolve()
    },
    [
      isEditing,
      isDisplayNewBSPatioForm,
      validateBSPatioForm,
      selectedBSPatioForm,
      openInfoSnackbar,
      bsPatioList,
      bsPatioFormToDto,
      submitBSPatioDtoList,
      resetForm,
      setIsOpen,
    ]
  )

  return (
    <BSFormDialog
      title="Créer un patio"
      open={isOpen}
      formId="create-bs-patio"
      formContent={
        <>
          <Grid item xs={12} sx={{ display: "flex", justifyContent: "flex-start", alignItems: "center" }}>
            <Box>
              <Typography fontSize="14px" sx={{ color: customPalette.mediumGreen3 }}>
                {`PATIO ${bsPatioList && bsPatioList.length + 1}`}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={4}>
            <FormNumberInput
              fieldName="surface"
              label="Surface (m²)"
              value={selectedBSPatioForm.surface}
              handleChange={(value) => handleFieldChange("surface", stringToNumber(value))}
              errors={errors}
            />
          </Grid>
          <Grid item xs={4}>
            <Tooltip title="Le 0 correspond au rez-de-chaussée." placement="top" arrow>
              <span style={{ width: "100%" }}>
                <FormNumberInput
                  fieldName="startingLevelNumber"
                  label="Niveau de départ"
                  value={selectedBSPatioForm.startingLevelNumber}
                  handleChange={(value) => handleFieldChange("startingLevelNumber", stringToNumber(value))}
                  errors={errors}
                  decimalScale={0}
                />
              </span>
            </Tooltip>
          </Grid>
          <Grid item xs={4}>
            <FormNumberInput
              fieldName="impactedFloor"
              label="Nombre d'étages impactés"
              value={selectedBSPatioForm.impactedFloor}
              handleChange={(value) => handleFieldChange("impactedFloor", stringToNumber(value))}
              errors={errors}
              decimalScale={0}
            />
          </Grid>
        </>
      }
      isSubmitting={isSubmitting}
      onClose={() => {
        handleClose()
        resetForm()
      }}
      onSubmit={handleSubmit}
    />
  )
}
